import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import client from "@/api/client";
import { toast } from "sonner";

/**
 * useBulkSelection — a tiny selection + POST-to-bulk helper reused by
 * every admin list. Backend contract:  POST /admin/bulk/{kind}/{action}
 * with { ids, value? } and returns { affected }.
 *
 * @param kind      backend `kind` string (see routes_bulk.py _KINDS).
 * @param getId     (item) => string  — extracts the row id.
 * @param onDone    callback fired after every successful bulk call
 *                  (typically the list's `reload()`).
 */
export function useBulkSelection(kind, getId, onDone) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(null);

  const isSelected = useCallback((id) => selected.has(id), [selected]);
  const toggle = useCallback((id) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);
  const toggleAll = useCallback((items) => {
    const ids = items.map(getId);
    setSelected((s) => {
      const allSelected = ids.every((i) => s.has(i));
      if (allSelected) return new Set([...s].filter((i) => !ids.includes(i)));
      return new Set([...s, ...ids]);
    });
  }, [getId]);
  const clear = useCallback(() => setSelected(new Set()), []);

  const run = useCallback(async (action, value) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(action);
    try {
      const body = { ids };
      if (value !== undefined) body.value = value;
      const res = await client.post(`/admin/bulk/${kind}/${action}`, body);
      const n = res.data?.affected ?? 0;
      toast.success(t("common.bulkDone", { count: n }));
      clear();
      if (onDone) await onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.bulkFailed"));
    } finally {
      setBusy(null);
    }
  }, [kind, selected, clear, onDone, t]);

  return { selected, isSelected, toggle, toggleAll, clear, run, busy, count: selected.size };
}

/**
 * BulkToolbar — renders the bar when count > 0. `actions` is an array
 * of { key, label, icon, variant, requiresConfirm, confirmTitle,
 *      confirmBody, value } items. The bar is intentionally boring —
 * every list uses the same visual language for bulk edits.
 */
export function BulkToolbar({ count, actions, run, busy, onClear, testId = "bulk-toolbar" }) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <div
      className="rounded-xl border border-primary/30 bg-primary/5 px-3 py-2 flex items-center gap-2 flex-wrap"
      data-testid={testId}
    >
      <span className="text-sm font-medium" data-testid={`${testId}-count`}>
        {t("common.nSelected", { count })}
      </span>
      <div className="ml-auto flex items-center gap-2 flex-wrap">
        {actions.map((a) => {
          const isBusy = busy === a.key;
          const btn = (
            <Button
              key={`${a.key}-${a.value ?? ""}`}
              size="sm"
              variant={a.variant || "outline"}
              className="rounded-lg gap-1"
              disabled={!!busy}
              onClick={a.requiresConfirm ? undefined : () => run(a.key, a.value)}
              data-testid={`${testId}-${a.key}${a.value ? `-${a.value}` : ""}`}
            >
              {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (a.icon || null)}
              {a.label}
            </Button>
          );
          if (!a.requiresConfirm) return btn;
          return (
            <AlertDialog key={`${a.key}-${a.value ?? ""}`}>
              <AlertDialogTrigger asChild>{btn}</AlertDialogTrigger>
              <AlertDialogContent data-testid={`${testId}-${a.key}-dialog`}>
                <AlertDialogHeader>
                  <AlertDialogTitle>{a.confirmTitle || `${a.label} ${count}?`}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {a.confirmBody || t("common.cannotUndo")}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid={`${testId}-${a.key}-cancel`}>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction
                    className={a.variant === "destructive" ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
                    onClick={() => run(a.key, a.value)}
                    data-testid={`${testId}-${a.key}-confirm`}
                  >
                    {a.label}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          );
        })}
        <Button size="sm" variant="ghost" className="rounded-lg" onClick={onClear} data-testid={`${testId}-clear`}>
          {t("common.clear")}
        </Button>
      </div>
    </div>
  );
}
